import { useState } from "react";
import {
  Drawer,
  Tabs,
  Form,
  InputNumber,
  Input,
  Switch,
  Button,
  Select,
  Table,
  Popconfirm,
  Space,
  Descriptions,
} from "antd";

import { formatBalance } from "../../../utils/formatters";
import { useCorrectionForm } from "./useCorrectionForm";

import type { GetBalanceQuery } from "@/graphql";

type BalanceData = GetBalanceQuery["getBalance"];

interface CorrectionDrawerProps {
  open: boolean;
  onClose: () => void;
  balance: BalanceData | undefined;
  refetch: () => void;
}

export function CorrectionDrawer({
  open,
  onClose,
  balance,
  refetch,
}: CorrectionDrawerProps) {
  const handleSuccess = () => {
    refetch();
    onClose();
  };

  return (
    <Drawer
      title="Данс залруулга"
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
    >
      <Tabs
        items={[
          {
            key: "cash",
            label: "Мөнгөн залруулга",
            children: (
              <CashCorrectionTab balance={balance} onSuccess={handleSuccess} />
            ),
          },
          {
            key: "bond",
            label: "Бондын хүү залруулга",
            children: (
              <BondCorrectionTab balance={balance} onSuccess={handleSuccess} />
            ),
          },
          {
            key: "stock",
            label: "Хувьцаа залруулга",
            children: (
              <StockCorrectionTab balance={balance} onSuccess={handleSuccess} />
            ),
          },
        ]}
      />
    </Drawer>
  );
}

// ─── Cash Correction Tab ────────────────────────────────────────────────

function CashCorrectionTab({
  balance,
  onSuccess,
}: {
  balance: BalanceData | undefined;
  onSuccess: () => void;
}) {
  const [form] = Form.useForm();
  const { cashWithdrawal } = useCorrectionForm({ onSuccess });
  const [fromHold, setFromHold] = useState(false);

  const userId = (balance?.user as any)?._id;

  return (
    <div>
      <Descriptions
        bordered
        size="small"
        column={2}
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item label="Бэлэн мөнгө">
          {formatBalance({ balance: balance?.cashAmount ?? 0 })} ₮
        </Descriptions.Item>
        <Descriptions.Item label="Худалдан авалтаас үлдсэн дүн">
          {formatBalance({ balance: balance?.holdAmount ?? 0 })} ₮
        </Descriptions.Item>
      </Descriptions>

      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          cashWithdrawal.mutate({
            input: {
              userId,
              amount: Number(values.amount),
              reason: values.reason,
              fromHold,
            },
          });
        }}
      >
        <Form.Item label="Худалдан авалтаас үлдсэн дүнгээс авах">
          <Switch checked={fromHold} onChange={setFromHold} />
          <span style={{ marginLeft: 8 }}>
            {fromHold ? "holdAmount-аас" : "cashAmount-аас"}
          </span>
        </Form.Item>

        <Form.Item
          name="amount"
          label="Дүн"
          rules={[
            { required: true, message: "Дүн оруулна уу" },
            {
              type: "number",
              min: 1,
              message: "Дүн 0-ээс их байх ёстой",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Зарлага хийх дүн"
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
          />
        </Form.Item>

        <Form.Item
          name="reason"
          label="Шалтгаан"
          rules={[{ required: true, message: "Шалтгаан оруулна уу" }]}
        >
          <Input.TextArea rows={3} placeholder="Залруулгын шалтгаан" />
        </Form.Item>

        <Popconfirm
          title="Залруулга хийхдээ итгэлтэй байна уу?"
          onConfirm={() => form.submit()}
        >
          <Button type="primary" danger loading={cashWithdrawal.isLoading}>
            Залруулга хийх
          </Button>
        </Popconfirm>
      </Form>
    </div>
  );
}

// ─── Bond Correction Tab ────────────────────────────────────────────────

function BondCorrectionTab({
  balance,
  onSuccess,
}: {
  balance: BalanceData | undefined;
  onSuccess: () => void;
}) {
  const [form] = Form.useForm();
  const { bondPreview, bondCorrection } = useCorrectionForm({ onSuccess });
  const [previewData, setPreviewData] = useState<any>(null);
  const [selectedBondIndex, setSelectedBondIndex] = useState<number | null>(
    null,
  );

  const userId = (balance?.user as any)?._id;
  const bonds = balance?.bonds ?? [];
  const selectedBond =
    selectedBondIndex !== null ? bonds[selectedBondIndex] : null;

  const handlePreview = (values: any) => {
    bondPreview.mutate(
      {
        input: {
          userId,
          bondIndex: values.bondIndex,
          correctedAmount: Number(values.correctedAmount),
          historyIndex: values.historyIndex,
          reason: values.reason,
        },
      },
      {
        onSuccess: (data) => {
          setPreviewData(data.previewBondInterestRecalc);
        },
      },
    );
  };

  const handleCorrection = () => {
    const values = form.getFieldsValue();
    bondCorrection.mutate({
      input: {
        userId,
        bondIndex: values.bondIndex,
        correctedAmount: Number(values.correctedAmount),
        historyIndex: values.historyIndex,
        reason: values.reason,
      },
    });
  };

  return (
    <div>
      <Form form={form} layout="vertical">
        <Form.Item
          name="bondIndex"
          label="Бонд сонгох"
          rules={[{ required: true, message: "Бонд сонгоно уу" }]}
        >
          <Select
            placeholder="Бонд сонгох"
            onChange={(value) => {
              setSelectedBondIndex(value);
              setPreviewData(null);
              form.setFieldsValue({ historyIndex: undefined });
            }}
          >
            {bonds.map((bond, index) => (
              <Select.Option key={index} value={index}>
                {bond.bond.name} (Хөрөнгө оруулалт:{" "}
                {formatBalance({ balance: bond.principleAmount })}₮)
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {selectedBond && (
          <Form.Item
            name="historyIndex"
            label="Түүхийн бичлэг сонгох"
            rules={[{ required: true, message: "Бичлэг сонгоно уу" }]}
          >
            <Select placeholder="Түүхийн бичлэг">
              {selectedBond.history.map((entry, index) => (
                <Select.Option key={index} value={index}>
                  #{index + 1} — Дүн:{" "}
                  {formatBalance({ balance: entry.principleAmount })}₮ (
                  {new Date(entry.createdAt).toLocaleDateString()})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="correctedAmount"
          label="Зөв principal дүн"
          rules={[
            { required: true, message: "Зөв дүн оруулна уу" },
            { type: "number", min: 0, message: "0-ээс их байх ёстой" },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Зөв хөрөнгө оруулалтын дүн"
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
          />
        </Form.Item>

        <Form.Item
          name="reason"
          label="Шалтгаан"
          rules={[{ required: true, message: "Шалтгаан оруулна уу" }]}
        >
          <Input.TextArea rows={2} placeholder="Залруулгын шалтгаан" />
        </Form.Item>

        <Space>
          <Button
            onClick={() => form.validateFields().then(handlePreview)}
            loading={bondPreview.isLoading}
          >
            Preview
          </Button>
        </Space>
      </Form>

      {previewData && (
        <div style={{ marginTop: 16 }}>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Үндсэн дүнгийн зөрүү">
              {formatBalance({ balance: previewData.totalPrincipalDifference })}{" "}
              ₮
            </Descriptions.Item>
            <Descriptions.Item label="Хүүгийн зөрүү">
              {formatBalance({ balance: previewData.totalDifference })} ₮
            </Descriptions.Item>
            <Descriptions.Item label="Нийт залруулга" span={2}>
              <strong>
                {formatBalance({ balance: previewData.totalCorrection })} ₮
              </strong>
            </Descriptions.Item>
          </Descriptions>

          <Table
            dataSource={previewData.entries}
            rowKey="historyEntryId"
            size="small"
            pagination={false}
            style={{ marginTop: 12 }}
            columns={[
              {
                title: "Principal",
                dataIndex: "principleAmount",
                render: (v: number) => (
                  <span>{formatBalance({ balance: v })} ₮</span>
                ),
              },
              {
                title: "Дүнгийн зөрүү",
                dataIndex: "principalDifference",
                render: (v: number) => (
                  <span>{formatBalance({ balance: v })} ₮</span>
                ),
              },
              {
                title: "Одоогийн хүү",
                dataIndex: "currentInterest",
                render: (v: number) => (
                  <span>{formatBalance({ balance: v })} ₮</span>
                ),
              },
              {
                title: "Залруулсан хүү",
                dataIndex: "correctedInterest",
                render: (v: number) => (
                  <span>{formatBalance({ balance: v })} ₮</span>
                ),
              },
              {
                title: "Хүүгийн зөрүү",
                dataIndex: "difference",
                render: (v: number) => (
                  <span>{formatBalance({ balance: v })} ₮</span>
                ),
              },
            ]}
          />

          <Popconfirm
            title="Бондын хүүг залруулахдаа итгэлтэй байна уу?"
            onConfirm={handleCorrection}
          >
            <Button
              type="primary"
              danger
              style={{ marginTop: 12 }}
              loading={bondCorrection.isLoading}
            >
              Залруулга хийх
            </Button>
          </Popconfirm>
        </div>
      )}
    </div>
  );
}

// ─── Stock Correction Tab ───────────────────────────────────────────────

function StockCorrectionTab({
  balance,
  onSuccess,
}: {
  balance: BalanceData | undefined;
  onSuccess: () => void;
}) {
  const [form] = Form.useForm();
  const { stockCorrection } = useCorrectionForm({ onSuccess });
  const [selectedStockIndex, setSelectedStockIndex] = useState<number | null>(
    null,
  );
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState<
    number | null
  >(null);

  const userId = (balance?.user as any)?._id;
  const stocks = balance?.stocks ?? [];
  const selectedStock =
    selectedStockIndex !== null ? stocks[selectedStockIndex] : null;
  const selectedEntry =
    selectedStock && selectedHistoryIndex !== null
      ? selectedStock.history[selectedHistoryIndex]
      : null;

  const handleCorrection = (values: any) => {
    stockCorrection.mutate({
      input: {
        userId,
        stockIndex: values.stockIndex,
        historyIndex: values.historyIndex,
        newTotalAmount: Number(values.newTotalAmount),
        reason: values.reason,
      },
    });
  };

  return (
    <div>
      <Form form={form} layout="vertical">
        <Form.Item
          name="stockIndex"
          label="Хувьцаа сонгох"
          rules={[{ required: true, message: "Хувьцаа сонгоно уу" }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Хувьцаа сонгох"
            onChange={(value) => {
              setSelectedStockIndex(value);
              setSelectedHistoryIndex(null);
              form.setFieldsValue({ historyIndex: undefined });
            }}
            options={stocks.map((stock, index) => ({
              value: index,
              label: `${stock.stock.name} (Тоо: ${stock.totalAmount.toFixed(6)}, Нийт: ${stock.totalPrice.toLocaleString()}₮)`,
            }))}
          />
        </Form.Item>

        {selectedStock && (
          <Form.Item
            name="historyIndex"
            label="Түүхийн бичлэг сонгох"
            rules={[{ required: true, message: "Бичлэг сонгоно уу" }]}
          >
            <Select
              placeholder="Түүхийн бичлэг"
              onChange={(value) => setSelectedHistoryIndex(value)}
              options={selectedStock.history.map((entry, index) => ({
                value: index,
                label: `#${index + 1} — Тоо: ${entry.totalAmount.toFixed(6)} ш (${new Date(entry.createdAt).toLocaleDateString()})`,
              }))}
            />
          </Form.Item>
        )}

        <Form.Item
          name="newTotalAmount"
          label="Шинэ тоо ширхэг (ш)"
          rules={[
            { required: true, message: "Тоо оруулна уу" },
            { type: "number", min: 0, message: "0-ээс их байх ёстой" },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder={
              selectedEntry
                ? `Одоогийн: ${selectedEntry.totalAmount.toFixed(6)}`
                : "Шинэ тоо ширхэг"
            }
            step={0.000001}
          />
        </Form.Item>

        <Form.Item
          name="reason"
          label="Шалтгаан"
          rules={[{ required: true, message: "Шалтгаан оруулна уу" }]}
        >
          <Input.TextArea rows={2} placeholder="Залруулгын шалтгаан" />
        </Form.Item>

        <Popconfirm
          title="Хувьцааг залруулахдаа итгэлтэй байна уу?"
          onConfirm={() => form.validateFields().then(handleCorrection)}
        >
          <Button type="primary" danger loading={stockCorrection.isLoading}>
            Залруулга хийх
          </Button>
        </Popconfirm>
      </Form>
    </div>
  );
}
